// src/components/settings/DeliveryDistanceFeeCard.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import { deliveryFeeApi, storeApi } from '../../api';

// 按距离计费：和满减用的是完全同一套架构——总开关+门店来源这两项跟着
// shop_settings 一起存（settings.distance_fee_enabled / settings.delivery_fee_store_source /
// settings.default_delivery_store_id，由父组件 ShopSettingsPage 管理，跟着主
// "保存设置"按钮一起保存）；距离档位本身存在独立的 delivery_fee_tiers 表，
// 走单独的接口，这张卡片自己单独管理档位列表的状态，配一个独立的"保存运费规则"
// 按钮，不和外层的"保存设置"混在一起
const DeliveryDistanceFeeCard = ({ settings, onToggle, onChange }) => {
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [savingTiers, setSavingTiers] = useState(false);
  const [tierError, setTierError] = useState('');
  const [tierSuccess, setTierSuccess] = useState('');

  // 门店来源选"指定默认门店"时，需要一个门店下拉列表给商家选
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    loadTiers();
  }, []);

  useEffect(() => {
    if (settings.delivery_fee_store_source === 'fixed') loadBranches();
  }, [settings.delivery_fee_store_source]);

  const loadTiers = async () => {
    setLoadingTiers(true);
    try {
      const res = await deliveryFeeApi.getTiers();
      if (res.success) setTiers(res.data.length > 0 ? res.data : [{ min_distance_km: '', delivery_fee: '' }]);
      else setTiers([{ min_distance_km: '', delivery_fee: '' }]);
    } catch (err) {
      setTiers([{ min_distance_km: '', delivery_fee: '' }]);
    } finally {
      setLoadingTiers(false);
    }
  };

  const loadBranches = async () => {
    setLoadingBranches(true);
    try {
      const res = await storeApi.getList();
      if (res.success) setBranches((res.data || []).filter(s => s.status === 'active'));
    } catch (err) {
      console.error('加载门店列表失败:', err);
    } finally {
      setLoadingBranches(false);
    }
  };

  const addTierRow = () => {
    setTiers(prev => [...prev, { min_distance_km: '', delivery_fee: '' }]);
  };

  const removeTierRow = (index) => {
    setTiers(prev => prev.filter((_, i) => i !== index));
  };

  const updateTierRow = (index, field, value) => {
    setTiers(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const handleSaveTiers = async () => {
    setTierError('');
    setTierSuccess('');

    // 过滤掉两个字段都没填的空行
    const validTiers = tiers.filter(t => t.min_distance_km !== '' || t.delivery_fee !== '');
    for (const t of validTiers) {
      if (t.min_distance_km === '' || t.delivery_fee === '') {
        setTierError('请把每一档的起始距离和运费都填完整');
        return;
      }
    }

    setSavingTiers(true);
    try {
      const res = await deliveryFeeApi.saveTiers({ tiers: validTiers });
      if (res.success) {
        setTierSuccess('运费规则已保存');
        setTimeout(() => setTierSuccess(''), 3000);
        loadTiers();
      } else {
        setTierError(res.message || '保存失败');
      }
    } catch (err) {
      setTierError(err.message || '保存失败');
    } finally {
      setSavingTiers(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">按距离计费</h3>
          <p className="text-sm text-gray-500 mt-1">根据门店到客户收货地址的距离，分档收取运费</p>
        </div>
        <button
          onClick={() => onToggle('distance_fee_enabled')}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ml-4 ${
            settings.distance_fee_enabled ? 'bg-orange-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              settings.distance_fee_enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {settings.distance_fee_enabled && (
        <>
          {/* 门店坐标来源：自动计算最近分店 / 指定固定门店，二选一 */}
          <div className="border-t border-gray-100 pt-4">
            <span className="text-sm font-medium text-gray-700 block mb-2">门店坐标来源</span>
            <div className="flex gap-3 mb-3">
              <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                <input type="radio" name="delivery_fee_store_source" value="auto"
                  checked={settings.delivery_fee_store_source !== 'fixed'}
                  onChange={() => onChange('delivery_fee_store_source', 'auto')} />
                自动计算（找最近的分店）
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                <input type="radio" name="delivery_fee_store_source" value="fixed"
                  checked={settings.delivery_fee_store_source === 'fixed'}
                  onChange={() => onChange('delivery_fee_store_source', 'fixed')} />
                指定默认门店
              </label>
            </div>

            {settings.delivery_fee_store_source === 'fixed' && (
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                value={settings.default_delivery_store_id || ''}
                onChange={e => onChange('default_delivery_store_id', e.target.value)}
                disabled={loadingBranches}
              >
                <option value="">{loadingBranches ? '加载中...' : '请选择门店'}</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}{b.type === 'headquarters' ? '（总店）' : ''}</option>
                ))}
              </select>
            )}
          </div>

          {/* 距离档位 */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">距离档位</span>
              <button onClick={addTierRow}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                <Plus className="w-4 h-4" />添加一档
              </button>
            </div>

            {loadingTiers ? (
              <p className="text-sm text-gray-400 py-4 text-center">加载中...</p>
            ) : (
              <div className="space-y-2">
                {tiers.map((tier, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 whitespace-nowrap">距离≥</span>
                    <input type="number" step="0.1" min="0" placeholder="公里"
                      value={tier.min_distance_km}
                      onChange={e => updateTierRow(index, 'min_distance_km', e.target.value)}
                      className="w-20 px-2 py-1.5 border rounded-lg text-sm" />
                    <span className="text-sm text-gray-500 whitespace-nowrap">公里，运费</span>
                    <input type="number" step="0.01" min="0" placeholder="金额"
                      value={tier.delivery_fee}
                      onChange={e => updateTierRow(index, 'delivery_fee', e.target.value)}
                      className="w-24 px-2 py-1.5 border rounded-lg text-sm" />
                    <button onClick={() => removeTierRow(index)}
                      className="text-red-400 hover:text-red-600 ml-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2">
              例：距离≥0公里 运费$1，距离≥3公里 运费$2 —— 客户实际距离会匹配"不超过它的最大档位"
            </p>

            {tierError && <p className="text-sm text-red-500 mt-2">{tierError}</p>}
            {tierSuccess && <p className="text-sm text-green-600 mt-2">{tierSuccess}</p>}

            <button onClick={handleSaveTiers} disabled={savingTiers || loadingTiers}
              className="flex items-center gap-1.5 mt-3 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50">
              <Save className="w-4 h-4" />
              {savingTiers ? '保存中...' : '保存运费规则'}
            </button>
          </div>

          {/* 兜底提示：客户地址没有精确坐标时会发生什么，提前说清楚，避免商家
              以后疑惑"为什么这单没按距离算" */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <p className="text-xs text-amber-700">
              ⚠️ 若客户收货地址缺少精确定位信息（未在地图上选点），系统将自动改用上方"基础运费"
              作为兜底，不会按距离计算。
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default DeliveryDistanceFeeCard;